import { h as __h } from "preact";
import { Component, h } from "preact";
import { adminBillingResponse, adminInvoiceResponse } from "~/backend/validate.js";
/** @import { sessionEnterpriseResponse } from "~/backend/validate.js"; */
import { query, response } from "~/lib/api.js";
import html from "~/util/html.js";
import { pluralize } from "~/util/pluralize.js";
import { array } from "~/util/validate.js";
import Data from "~/component/Data.js";
import Icon from "~/component/Icon.js";
import Section from "~/component/Section.js";
/** @import { TriggerProps } from "~/component/Tooltip.js"; */
import Tooltip from "~/component/Tooltip.js";
import { Crumb } from "~/admin/component/Breadcrumbs.js";
import Table from "~/admin/component/Table.js";
import adminStyle from "~/admin/lib/style.js";
import color from "~/style/colors.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Billing.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Billing.module.css", import.meta.url);
const css = adopt(sheet);

// usage dates are date-only strings; format in UTC so the day doesn't shift
const dateFmt = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric",
  timeZone: "UTC"
});
const shortDateFmt = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC"
});

/** @param {number} n */
const round = n => Math.round(n * 10) / 10;

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {sessionEnterpriseResponse} enterprise
 */
export default class Billing extends Component {
  get #hasMetronomeCustomer() {
    return Boolean(/** @type {any} */this.props.enterprise.has_metronome_customer);
  }

  /** @type {any} */
  get #usageEndpoint() {
    const usage = this.#hasMetronomeCustomer ? "/usage" : "";
    return /** @type {const} */`/ui/admin/enterprises/${this.props.enterprise.id}/billing${usage}`;
  }
  #usage = query(() => ({
    key: [this.#usageEndpoint]
  }), {
    schema: response(adminBillingResponse)
  });
  #invoices = query(() => ({
    key: [(/** @type {any} */`/ui/admin/enterprises/${this.props.enterprise.id}/billing/invoices`)]
  }), {
    schema: response(array(adminInvoiceResponse))
  });
  render() {
    return [__h(Crumb, {}, "Billing"), __h("div", {
      "class": adminStyle.main
    }, __h("div", {
      "class": adminStyle.body + " " + css.page
    }, __h("div", {
      "class": css.comingSoon
    }, __h("h3", {
      "class": css.comingSoonHeader
    }, __h(Icon, {
      name: "potion"
    }), " Phaze is free while it's in beta."), __h("p", {
      "class": css.comingSoonNote
    }, "This is a preview of usage across your organizations. If anything doesn't look right,\n\t\t\t\t\t\t\tplease reach out to let us know."), __h("p", {
      "class": css.comingSoonNote
    }, "Thank you for helping us improve Phaze!")), __h(Section, {
      title: "Usage",
      icon: "graph"
    }, h(Data, {
      loading: this.#usage.loading && !this.#usage.data,
      error: this.#usage.error,
      children: this.#usage.data && __h(UsageChart, {
        data: this.#usage.data.data
      })
    })), this.#hasMetronomeCustomer && __h(Section, {
      title: "Invoices",
      icon: "receipt",
      fullBleed: true
    }, h(Data, {
      loading: this.#invoices.loading && !this.#invoices.data,
      error: this.#invoices.error,
      children: this.#invoices.data && __h(InvoiceTable, {
        invoices: this.#invoices.data.data
      })
    }))))];
  }
}

/**
 * @extends {Component<UsageChartProps>}
 *
 * @typedef {object} UsageChartProps
 * @property {adminBillingResponse} data
 */
class UsageChart extends Component {
  /** @override @param {UsageChartProps} nextProps */
  shouldComponentUpdate(nextProps) {
    return nextProps.data !== this.props.data;
  }
  get #chart() {
    const {
      organizations,
      usage
    } = this.props.data;

    /** @type {Record<string, string>} */
    const colors = {};
    for (const org of organizations) {
      colors[org.id] = color(org.name);
    }
    let maxHours = 0;
    /** @type {Record<string, number>} */
    const dayTotals = {};
    for (const day of usage) {
      let total = 0;
      for (const minutes of Object.values(day.minutes)) total += minutes / 60;
      dayTotals[day.date] = total;
      maxHours = Math.max(maxHours, total);
    }

    // round the axis max up to a clean number so the ticks are readable
    const yMax = Math.max(10, Math.ceil(maxHours / 10) * 10);
    const yTicks = [yMax, yMax / 2, 0];

    /** @type {Record<string, number>} */
    const totals = {};
    let grandTotal = 0;
    for (const org of organizations) {
      let total = 0;
      for (const day of usage) total += (day.minutes[org.id] ?? 0) / 60;
      totals[org.id] = round(total);
      grandTotal += total;
    }
    return {
      colors,
      dayTotals,
      yMax,
      yTicks,
      totals,
      grandTotal: round(grandTotal)
    };
  }
  render() {
    const {
      organizations,
      usage
    } = this.props.data;
    const {
      colors,
      dayTotals,
      yMax,
      yTicks,
      totals,
      grandTotal
    } = this.#chart;
    if (!grandTotal) {
      return __h("div", {
        "class": css.empty
      }, "None of your organizations have used Phaze yet.");
    }
    return [__h("div", {
      "class": css.chart,
      style: "--max: " + yMax
    }, __h("div", {
      "class": css.yAxis
    }, yTicks.map(tick => __h("div", {
      key: tick
    }, tick))), __h("div", {
      "class": css.bars
    }, usage.map(day => {
      const present = organizations.filter(org => day.minutes[org.id] !== undefined);
      const totalHours = dayTotals[day.date] ?? 0;
      const total = round(totalHours);
      return __h(Tooltip, {
        key: day.date,
        contents: [__h("div", {
          "class": css.tooltipDate
        }, dateFmt.format(new Date(day.date)), ": ", total, " ", pluralize(total, "hour")), __h("ul", {}, present.map(org => {
          const hours = round((day.minutes[org.id] ?? 0) / 60);
          return __h("li", {
            key: org.id,
            "class": css.orgRow,
            style: "--color: " + colors[org.id]
          }, org.name, ": ", hours, " ", pluralize(hours, "hour"));
        }))]
      }, (/** @type {TriggerProps} */trigger) => __h("button", {
        "class": css.bar,
        ...trigger
      }, __h("div", {
        "class": css.stack,
        style: "--total: " + totalHours
      }, present.map(org => __h("div", {
        key: org.id,
        "class": css.chunk,
        style: "--fill: " + colors[org.id] + "; --hours: " + (day.minutes[org.id] ?? 0) / 60
      })))));
    })), __h("div", {
      "class": css.xAxis
    }, usage.map((day, i) => __h("div", {
      key: day.date,
      "class": css.xLabel
    }, i % 3 === 0 ? shortDateFmt.format(new Date(day.date)) : "")))), __h("ul", {
      "class": css.legend
    }, __h("li", {}, __h("span", {
      "class": css.legendLabel
    }, "All organizations"), ": ", grandTotal, " ", pluralize(grandTotal, "hour")), organizations.map(org => __h("li", {
      key: org.id,
      "class": css.orgRow,
      style: "--color: " + colors[org.id]
    }, __h("span", {}, __h("span", {
      "class": css.legendLabel
    }, org.name), ": ", totals[org.id], " ", pluralize(totals[org.id] ?? 0, "hour")))))];
  }
}
const usdFmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD"
});

/** @param {adminInvoiceResponse} invoice */
function invoiceTotal(invoice) {
  // Metronome's fixed ID for its fiat "USD (cents)" pricing unit
  const USD_CENTS_ID = "2714e483-4ff1-48e4-9e25-ac732e8f24f2";
  if (invoice.credit_type_id === USD_CENTS_ID) return usdFmt.format(invoice.total / 100);
  return `${invoice.total} ${invoice.credit_type}`;
}

/**
 * @extends {Component<InvoiceTableProps>}
 *
 * @typedef {object} InvoiceTableProps
 * @property {adminInvoiceResponse[]} invoices
 */
class InvoiceTable extends Component {
  render() {
    const rows = this.props.invoices.map(invoice => {
      const issued = new Date(invoice.issued_at);
      return {
        key: invoice.id,
        label: `invoice ${invoice.id}`,
        value: invoice,
        columns: {
          period: `${shortDateFmt.format(new Date(invoice.start_at))} – ${shortDateFmt.format(new Date(invoice.end_at))}`,
          issued: issued.getTime() > 0 ? dateFmt.format(issued) : "—",
          status: __h("span", {
            "class": css.invoiceStatus
          }, invoice.status.toLowerCase()),
          total: invoiceTotal(invoice)
        }
      };
    });
    return h(/** @type {typeof Table<adminInvoiceResponse>} */Table, {
      label: "invoice",
      headers: [{
        id: "period",
        label: "Period"
      }, {
        id: "issued",
        label: "Issued"
      }, {
        id: "status",
        label: "Status"
      }, {
        id: "total",
        label: "Total"
      }],
      empty: "No invoices have been issued.",
      rows
    });
  }
}